import React from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { AriRule, CaseSummary, RollupDimension } from '~/services/AriAnomaliesService';

import { AnomalyCase, CaseStatus, PreValidationVerdict, RULE_DESCRIPTIONS, TIER_LABELS } from './types';
import { buildRatesVerifyUrl, buildVendorRoomRateDatesUrl, suggestedCheckIn } from './verifyUrl';

const STATUS_COLOURS: Record<CaseStatus, 'error' | 'warning' | 'success' | 'default'> = {
  pending: 'error',
  acknowledged: 'warning',
  resolved: 'success',
  dismissed: 'default',
  auto_resolved: 'success',
  suppressed: 'default',
};

const VERDICT_META: Record<
  PreValidationVerdict,
  { label: string; colour: 'error' | 'success' | 'warning' | 'default'; icon: React.ReactElement }
> = {
  confirmed: { label: 'Confirmed', colour: 'error', icon: <ReportProblemIcon fontSize="small" /> },
  self_resolved: { label: 'Self-resolved', colour: 'success', icon: <TaskAltIcon fontSize="small" /> },
  inconclusive: { label: 'Inconclusive', colour: 'warning', icon: <HelpOutlineIcon fontSize="small" /> },
  not_run: { label: 'Not checked', colour: 'default', icon: <HelpOutlineIcon fontSize="small" /> },
};

export interface CaseFilters {
  status: CaseStatus | 'all';
  /** what pre-validation concluded; 'all' leaves the queue unfiltered by verdict */
  verdict: PreValidationVerdict | 'all';
  tier: string;
  ruleKeys: Array<string>;
  /** set when arriving from the rollup, so the hand-off is visible and clearable */
  dimension: { field: RollupDimension; value: string } | null;
}

interface Props {
  /** one server-filtered, server-sorted page - the queue is far too long to hold in the browser */
  cases: Array<AnomalyCase>;
  total: number;
  rules: Array<AriRule>;
  summary?: CaseSummary;
  isLoading: boolean;
  isFetching: boolean;
  filters: CaseFilters;
  onFiltersChange: (filters: CaseFilters) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onOpenCase: (anomalyCase: AnomalyCase) => void;
  onStatusChange: (caseId: string, status: CaseStatus, reason?: string) => void;
  onDismiss: (anomalyCase: AnomalyCase) => void;
  onSuppress: (anomalyCase: AnomalyCase) => void;
}

export default function CaseList({
  cases,
  total,
  rules,
  summary,
  isLoading,
  isFetching,
  filters,
  onFiltersChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onOpenCase,
  onStatusChange,
  onDismiss,
  onSuppress,
}: Props) {
  const { status: statusFilter, tier: tierFilter, ruleKeys } = filters;
  const setStatusFilter = (status: CaseStatus | 'all') => onFiltersChange({ ...filters, status });
  const setTierFilter = (tier: string) => onFiltersChange({ ...filters, tier });
  const setRuleKeys = (next: string | Array<string>) =>
    onFiltersChange({ ...filters, ruleKeys: typeof next === 'string' ? next.split(',') : next });
  const ruleLabel = (key: string) => rules.find((rule) => rule.ruleKey === key)?.label ?? key;

  /**
   * Every tile counts the pending queue, so a tile applies its own dimension AND pins status
   * to pending. Leaving a wider status in place would return more rows than the tile
   * advertises, and a count that does not match the list it produces is worse than one that
   * does not click. Clicking an active tile clears that dimension again.
   */
  const tiles: Array<{ label: string; value?: number; isActive: boolean; apply: () => CaseFilters }> = [
    {
      label: 'Pending',
      value: summary?.pending,
      isActive: statusFilter === 'pending',
      apply: () => ({ ...filters, status: statusFilter === 'pending' ? 'all' : 'pending' }),
    },
    {
      label: 'Pre-validated as real',
      value: summary?.confirmed,
      isActive: filters.verdict === 'confirmed',
      apply: () =>
        filters.verdict === 'confirmed'
          ? { ...filters, verdict: 'all' }
          : { ...filters, status: 'pending', verdict: 'confirmed' },
    },
    {
      label: 'Tier 1 properties',
      value: summary?.tier1,
      isActive: tierFilter === 'tier_1',
      apply: () =>
        tierFilter === 'tier_1' ? { ...filters, tier: 'all' } : { ...filters, status: 'pending', tier: 'tier_1' },
    },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Cases open in <strong>pending</strong>. The system pre-validates before anyone sees them — checking whether the
        rate is live in market, comparing against bedbank logs, and looking at recent sales — so a case that fixed
        itself never reaches a human.
      </Alert>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <Paper
            key={t.label}
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: t.isActive ? 'primary.main' : undefined,
              bgcolor: t.isActive ? 'action.selected' : undefined,
            }}
          >
            <ButtonBase
              onClick={() => onFiltersChange(t.apply())}
              aria-pressed={t.isActive}
              sx={{ display: 'block', width: '100%', p: 2, textAlign: 'left' }}
            >
              <Typography variant="body2" color="text.secondary">
                {t.label}
              </Typography>
              <Typography variant="h5">{t.value?.toLocaleString() ?? '—'}</Typography>
            </ButtonBase>
          </Paper>
        ))}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="case-status">Status</InputLabel>
          <Select
            labelId="case-status"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="case-tier">Property tier</InputLabel>
          <Select
            labelId="case-tier"
            label="Property tier"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <MenuItem value="all">All tiers</MenuItem>
            <MenuItem value="tier_1">Tier 1</MenuItem>
            <MenuItem value="tier_2">Tier 2</MenuItem>
            <MenuItem value="tier_3">Tier 3</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 260, maxWidth: 420 }}>
          <InputLabel id="case-rule">Alert type</InputLabel>
          <Select
            multiple
            labelId="case-rule"
            label="Alert type"
            value={ruleKeys}
            onChange={(e) => setRuleKeys(e.target.value)}
            renderValue={(selected) => selected.map(ruleLabel).join(', ')}
          >
            {rules.map((rule) => (
              <MenuItem key={rule.ruleKey} value={rule.ruleKey}>
                <Checkbox size="small" checked={ruleKeys.includes(rule.ruleKey)} />
                <ListItemText primary={rule.label} secondary={rule.description} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filters.dimension && (
          <Chip
            color="primary"
            variant="outlined"
            label={filters.dimension.value}
            onDelete={() => onFiltersChange({ ...filters, dimension: null })}
          />
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Issue</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Affected rate</TableCell>
              <TableCell>Pre-validation</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Verify</TableCell>
              <TableCell align="right">Triage</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {cases.map((c) => {
              const verdict = VERDICT_META[c.preValidation.verdict];
              const checkIn = suggestedCheckIn(c.firstStayDate, c.lastStayDate);

              return (
                <TableRow key={c.caseId} hover sx={{ cursor: 'pointer' }} onClick={() => onOpenCase(c)}>
                  <TableCell>
                    <Tooltip title={RULE_DESCRIPTIONS[c.ruleKey] ?? ''}>
                      <Typography variant="body2" fontWeight={700}>
                        {c.ruleLabel}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {c.caseId}
                      {c.occurrences > 1 && ` · seen ${c.occurrences}x`}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{c.propertyName}</Typography>
                      {c.propertyTier === 'tier_1' && (
                        <Chip size="small" color="primary" label={TIER_LABELS[c.propertyTier]} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {c.vendorBrand} · {c.territory} · {c.channel}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{c.ratePlanName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.roomTypeName}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {c.rateRank && (
                        <Tooltip title={`${c.bookings12m} bookings in the last 12 months`}>
                          <Chip
                            size="small"
                            color={c.rateRank <= 3 ? 'error' : 'default'}
                            icon={<LocalOfferIcon />}
                            label={`#${c.rateRank} booked`}
                          />
                        </Tooltip>
                      )}
                      {c.hasMealPlan && <Chip size="small" variant="outlined" label="meal plan" />}
                      {!c.isLeadRate && c.rateRank === 1 && (
                        <Tooltip title="Most-booked rate but not the lead rate — invisible to lead-rate monitoring">
                          <Chip size="small" variant="outlined" color="warning" label="not lead" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Tooltip title={c.preValidation.checks.map((ck) => `${ck.label}: ${ck.detail}`).join(' · ')}>
                      <Chip size="small" color={verdict.colour} icon={verdict.icon} label={verdict.label} />
                    </Tooltip>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {c.preValidation.checksAgreeing ?? c.preValidation.checks.filter((ck) => ck.supportsAlert).length}{' '}
                      of {c.preValidation.checksApplicable ?? c.preValidation.checks.length} checks agree
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip size="small" label={c.status} color={STATUS_COLOURS[c.status]} variant="outlined" />
                    {c.dismissReason && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.dismissReason}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={checkIn ? `Check the rate live for ${checkIn}` : 'No affected stay dates'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!checkIn}
                            endIcon={<LaunchIcon />}
                            href={
                              checkIn
                                ? buildRatesVerifyUrl({
                                    accommodationPropertyId: c.accommodationPropertyId,
                                    checkIn,
                                  })
                                : undefined
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Rate
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Open the room-rate dates calendar">
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<CalendarMonthIcon />}
                          href={buildVendorRoomRateDatesUrl({
                            vendorId: c.vendorId,
                            propertyId: c.propertyId,
                            roomTypeId: c.roomTypeId,
                            roomRateId: c.roomRateId,
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Dates
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Acknowledge — I am on it">
                        <Button
                          size="small"
                          disabled={c.status !== 'pending'}
                          onClick={() => onStatusChange(c.caseId, 'acknowledged')}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Resolve — fixed at the supplier">
                        <Button
                          size="small"
                          color="success"
                          disabled={c.status === 'resolved'}
                          onClick={() => onStatusChange(c.caseId, 'resolved')}
                        >
                          <DoneAllIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Dismiss — known issue or not real">
                        <Button
                          size="small"
                          color="warning"
                          disabled={c.status === 'dismissed'}
                          onClick={() => onDismiss(c)}
                        >
                          <NotInterestedIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Suppress — silence this shape of alert for a while">
                        <Button
                          size="small"
                          color="warning"
                          disabled={c.status === 'suppressed'}
                          onClick={() => onSuppress(c)}
                        >
                          <VolumeOffIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => onPageChange(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {(isLoading || isFetching) && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {!isLoading && total === 0 && <Alert severity="success">Nothing to triage in this view.</Alert>}
    </Box>
  );
}
